// @ts-strict-ignore
import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";

import { Tag, FloatingLabelInput, FloatingLabelSelect, Skeleton } from "@aviary";
import type {
  IngredientUnit,
  IngredientFilterV2,
  IngredientFilter,
} from "@shared/components/CatalogSearch/CatalogSearch";
import { useIngredientUnitsQuery } from "@shared/data/queries/IngredientUnitsQuery/IngredientUnits.query";
import { useCatalogSearch } from "@shared/hooks/useCatalogSearch/useCatalogSearch";
import { useDebounce } from "@shared/hooks/useDebounce/useDebounce";
import { useHandleIngredientSelect } from "@shared/hooks/useHandleIngredientSelect/useHandleIngredientSelect";
import { useIngredientRemove } from "@shared/hooks/useIngredientRemove/useIngredientRemove";
import { l } from "@shared/locales/i18n";
import { Navigate } from "@shared/react-router-dom/react-router-dom";

import * as styles from "./IngredientDosage.style";

interface UnitDosage {
  unitNum: 1 | 2;
  unitValue: string;
}

interface Props {
  ingredient: IngredientFilterV2;
}

interface ValidateInput {
  isValid: boolean;
  value: number;
}

const IngredientDosage = ({ ingredient }: Props) => {
  const { t } = useTranslation();
  const { removeIngredient } = useIngredientRemove();
  const { onlyIngredient, activeIngredients } = useCatalogSearch();

  const [unitDosage, setUnitDosage] = useState<UnitDosage>({
    unitNum: 2,
    unitValue: "mg",
  });

  const [minDosage, setMinDosage] = useState<number>(
    ingredient?.searchAttributes?.addon?.minDose ?? null
  );
  const [maxDosage, setMaxDosage] = useState<number>(
    ingredient?.searchAttributes?.addon?.maxDose ?? null
  );

  const { data, loading, error } = useIngredientUnitsQuery({
    variables: { ingredientId: ingredient.searchAttributes.id },
    onCompleted: unitData => {
      const { unitOne, unitTwo } = unitData?.viewer?.ingredientUnits ?? {};
      if (unitOne) {
        setUnitDosage({ unitNum: 1, unitValue: unitOne });
      } else if (unitTwo) {
        setUnitDosage({ unitNum: 2, unitValue: unitTwo });
      }
    },
  });

  const getIngredientUnit = (domUnit?: UnitDosage): IngredientUnit => {
    const selectedUnit: UnitDosage = domUnit ?? unitDosage;

    if (selectedUnit.unitNum === 1) {
      return {
        unitOne: selectedUnit.unitValue,
      };
    }

    return {
      unitTwo: selectedUnit.unitValue,
    };
  };

  const { handleIngredientSelectionV2 } = useHandleIngredientSelect();

  const setIngredientDosage = useDebounce(
    (
      ingredientSelected: IngredientFilterV2,
      currentActiveIngredients: IngredientFilterV2[],
      minDose?: number,
      maxDose?: number,
      unit?: IngredientUnit
    ) => {
      const distanceFromTop = window?.scrollY;
      handleIngredientSelectionV2(
        {
          minDose,
          maxDose,
          unit,
          ingredientSelected,
        },
        currentActiveIngredients
      );
      requestAnimationFrame?.(() => window?.scrollTo(0, distanceFromTop));
    },
    500
  );

  const isPositiveInteger = (value: number) => Number.isInteger(value) && value >= 0;

  const validateInput = (value): ValidateInput => {
    if (value === "") {
      return { isValid: true, value: null };
    }

    const numValue = Number(value);

    return {
      isValid: isPositiveInteger(numValue),
      value: numValue,
    };
  };

  const onMinDosageChange = event => {
    const input = validateInput(event.target.value);
    if (!input.isValid) return;
    setMinDosage(input.value);
    if (!unitDosage.unitValue) return;
    setIngredientDosage(ingredient, activeIngredients, input.value, maxDosage, getIngredientUnit());
  };

  const onMaxDosageChange = event => {
    const input = validateInput(event.target.value);
    if (!input.isValid) return;
    setMaxDosage(input.value);
    if (!unitDosage.unitValue) return;
    setIngredientDosage(ingredient, activeIngredients, minDosage, input.value, getIngredientUnit());
  };

  const onUnitDosageChange = event => {
    const selectedOption = event.target.selectedOptions[0];
    const unitValue = selectedOption.text;
    const unitNum = selectedOption.dataset.unitNum;
    const selectedUnit = { unitNum, unitValue };
    setUnitDosage(selectedUnit);
    if (!minDosage && !maxDosage) return;
    setIngredientDosage(
      ingredient,
      activeIngredients,
      minDosage,
      maxDosage,
      getIngredientUnit(selectedUnit)
    );
  };

  if (error) return <Navigate to="/" replace />;

  if (loading) {
    return (
      <div css={styles.container}>
        <Skeleton type="button" isFullWidth />
        <div css={styles.dosageMinMax}>
          <Skeleton type="button" isFullWidth />
          <Skeleton type="button" isFullWidth />
        </div>
        <Skeleton type="button" isFullWidth />
      </div>
    );
  }

  const { unitOne, unitTwo } = data?.viewer?.ingredientUnits ?? {};

  const renderUnitDosageOptions = () => {
    const renderUnitOption = (unit: string, unitNum: number) => (
      <option key={unit} value={unit} data-unit-num={unitNum}>
        {unit}
      </option>
    );

    const options = [];

    if (unitOne) {
      options.push(renderUnitOption(unitOne, 1));
    }

    if (unitTwo) {
      options.push(renderUnitOption(unitTwo, 2));
    }

    return options;
  };

  const onRemoveIngredient = () => {
    const distanceFromTop = window?.scrollY;
    removeIngredient(ingredient as IngredientFilter, false);
    requestAnimationFrame?.(() => window?.scrollTo(0, distanceFromTop));
  };

  const getIngredientText = () => {
    if (onlyIngredient) {
      return (
        <Trans i18nKey={l.sharedCatalog.filter.Only}>
          Only <span css={styles.bold}>{{ ingredientName: ingredient.name }}</span>
        </Trans>
      );
    }

    return ingredient.name;
  };

  return (
    <div css={styles.container}>
      <Tag
        css={styles.dosageName}
        text={getIngredientText()}
        handleClick={onRemoveIngredient}
        noEllipses
      />

      <div css={styles.dosageMinMax}>
        <FloatingLabelInput
          label={t(l.sharedCatalog.filter.dosageMin)}
          type="text"
          value={minDosage ?? ""}
          onChange={onMinDosageChange}
          id={`${ingredient.searchAttributes.id}-mix`}
          hasWhiteBackground
          min={0}
        />
        <FloatingLabelInput
          label={t(l.sharedCatalog.filter.dosageMax)}
          type="text"
          value={maxDosage ?? ""}
          onChange={onMaxDosageChange}
          id={`${ingredient.searchAttributes.id}-max`}
          hasWhiteBackground
          min={0}
        />
      </div>

      <FloatingLabelSelect
        value={unitDosage.unitValue}
        label={t(l.sharedCatalog.filter.dosageUnit)}
        id={`${ingredient.searchAttributes.id}-unit`}
        hasWhiteBackground
        handleChange={onUnitDosageChange}
      >
        {renderUnitDosageOptions()}
      </FloatingLabelSelect>
    </div>
  );
};

export { IngredientDosage };
